'use client'

import { useCart, useModals } from '@/lib/hooks'
import { cn, formatPrice } from '@/lib/utils'
import { Pencil, X } from 'lucide-react'
import React, { useMemo } from 'react'

import {
  Badge,
  BarcodeBadge,
  Button,
  Separator,
  SkuBadge,
} from '@/components/ui'

interface ProductCardProps extends React.ComponentPropsWithoutRef<'div'> {
  product: any
  priceBadgeClassName?: string
  headingClassName?: string
  priceClassName?: string
  isOnPreview?: boolean
}

export const ProductCardMini = React.memo(({
  product = {} as Product,
  className,
  isOnPreview = false,
  ...restProps
}: ProductCardProps) => {
  const { openModal } = useModals()
  const { setProduct } = useCart()

  const fallbackTitle = useMemo(
    () => `${product?.product_name ?? product?.title} ${product?.varient_options}`,
    [product?.product_name, product?.title, product?.varient_options]
  )

  const groupedAddons = useMemo(() => {
    if (!product?.addon_options?.length) return []
    
    return product.addon_options.reduce(
      (acc: any, addon: any) => {
        const existing = acc.find((item: any) => item.id === addon.id)
        if (existing) {
          existing.quantity += 1
          existing.totalPrice += addon.price
        } else {
          acc.push({
            ...addon,
            quantity: 1,
            totalPrice: addon.price,
          })
        }
        return acc
      },
      [],
    )
  }, [product?.addon_options])

  return (
    <div
      className={cn(
        'flex gap-3 rounded-lg border border-gray-100 bg-white p-3.5 shadow-card dark:border-gray-700',
        className,
      )}
      {...restProps}
    >
      <div className="flex pt-1">
        <Badge size="lg" className="w-[32px] gap-0 p-0">
          {product.quantity ?? 1}
          <X className="relative size-2.5 shrink-0" strokeWidth={2} />
        </Badge>
      </div>
      <div className="flex w-full flex-col gap-2 lg:gap-0.5">
        <h2 className="line-clamp-1 text-sm font-medium">
          <span>{product.variant_label ?? fallbackTitle}</span>
        </h2>

        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
            <SkuBadge
              badge={{
                className: cn('border-gray-600 text-gray-700', {
                  'w-[40px]': product.warranty?.serial_number,
                }),
              }}
              className="text-xs font-medium text-gray-600"
            >
              {product.sku}
            </SkuBadge>
            <BarcodeBadge className="text-xs font-medium text-gray-600">
              {product.barcode}
            </BarcodeBadge>
          </div>

          {product.warranty?.serial_number && (
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-2">
              <Badge
                className={cn('w-[40px] border-gray-600 text-gray-700')}
                variant="outline"
                size="md"
              >
                Serial
              </Badge>
              <span className="font-mono text-xs font-medium leading-[1.2] text-gray-600">
                {product.warranty?.serial_number}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {product.sub_total !== product.total && (
                <span className="font-mono text-sm font-medium text-red-500 line-through">
                  {formatPrice(product.sub_total, 2, false)}
                </span>
              )}
              <span className="font-mono text-sm font-medium">
                {formatPrice(product.total, 2, false)}
              </span>
            </div>

            {Number(product.discount_total) > 0 && (
              <span className="font-mono text-sm font-medium text-green-500">
                ({product.discount_total} off)
              </span>
            )}
          </div>

          {(product?.has_warranty === 1 || product?.has_warranty === true) &&
            product?.warranty?.serial_number === null &&
            !isOnPreview && (
              <div className="flex w-full pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-full border-yellow-300 !bg-yellow-50 px-2 text-yellow-600"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    setProduct(product)
                    openModal('ADD_CART_ITEM_SERIAL')
                  }}
                >
                  <Pencil className="!size-3" />
                  <span className="!text-xs">Enter warranty serial number</span>
                </Button>
              </div>
            )}
        </div>

        {groupedAddons.length > 0 && (
          <>
            <div className="relative flex w-full py-3">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 bg-white py-1 pr-2 text-xs font-medium text-gray-600">
                Addons
              </span>
              <Separator />
            </div>
            <div className="flex w-full flex-col">
              {groupedAddons.map((addon: any) => (
                <div
                  key={addon?.id}
                  className="flex w-full items-center justify-between gap-2"
                >
                  <span className="text-sm font-medium">
                    — {addon?.name}
                    {addon.quantity > 1 && (
                      <span className="ml-1 text-sm font-medium text-gray-500">
                        ×{addon.quantity}
                      </span>
                    )}
                  </span>
                  <span className="text-sm font-medium">
                    {formatPrice(addon?.totalPrice, 2, false)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
})
